/**
 * What a drop meant, decided by where it landed.
 *
 * This is the rule decision 44 is built on — *one gesture, two jobs* — and it is a pure function on
 * purpose. dnd-kit needs real layout rectangles, which jsdom does not have, so a test that drags
 * anything is a test that cannot run here. Keeping the decision out of the components means the part
 * that can be wrong is still covered: the wiring hands over two nodes, and everything interesting
 * happens in four comparisons.
 *
 * One context holds both lists, so a node has to say which list it is from. That is what dnd-kit's
 * `data` is for — the alternative, encoding the kind into the id, needed a parser and made a
 * filename's punctuation a correctness concern.
 *
 * `entity` dropped on `media` is deliberately nothing. It reads as "attach" from the other side, but
 * the file is the thing being carried, and a second way to say the same thing is a second code path
 * that can disagree.
 */
export const MEDIA_DRAG = { kind: "media" } as const;
export const ENTITY_DRAG = { kind: "entity" } as const;

/** The shape of dnd-kit's `active` and `over`, without depending on dnd-kit for it. */
interface DragNode {
  id: string | number;
  data: { current?: { kind?: string } | null };
}

export type DropIntent =
  | { kind: "reorder-media"; activeId: string; overId: string }
  | { kind: "reorder-entities"; activeId: string; overId: string }
  | { kind: "attach"; mediaId: string; entityId: string }
  | null;

export const dropIntent = (
  active: DragNode,
  over: DragNode | null | undefined,
): DropIntent => {
  if (!over) return null;

  const from = active.data.current?.kind;
  const to = over.data.current?.kind;
  const activeId = String(active.id);
  const overId = String(over.id);

  // A card dropped on itself, which is what a click that moved 8px looks like.
  if (activeId === overId) return null;

  if (from === "media" && to === "media") {
    return { kind: "reorder-media", activeId, overId };
  }
  if (from === "media" && to === "entity") {
    return { kind: "attach", mediaId: activeId, entityId: overId };
  }
  if (from === "entity" && to === "entity") {
    return { kind: "reorder-entities", activeId, overId };
  }

  return null;
};
